import * as React from 'react';
import { connect } from 'react-redux';
import { sessionSelector } from '../reducers/session';
import { UserRole } from '../types';
import { getDisplayName } from '../utils';

// detailed props are hard to determined from here
type OwnProps = any;

export type StoreProps = {
  roles: Array<UserRole>;
};

type Props = StoreProps;

/**
 * HOC that returns the wrapped component only if the user has one of the specified roles
 * @param {array} allowedRoles - user roles that are allowed to see the page
 * @returns {Component} the wrapped component or a 'not authorized' component
 */
const authorizerForRoles = <AProps extends {} = any>(
  allowedRoles: Array<UserRole>,
) => {
  return (
    AuthorizedComponent: React.ComponentType<AProps>,
    UnauthorizedComponent: React.ComponentType,
  ) => {
    class withAuthorization extends React.Component<Props> {
      render() {
        const { roles, ...rest } = this.props;

        if (allowedRoles.some((role) => roles.includes(role))) {
          // type assertion: an error I don't quite understand
          return <AuthorizedComponent {...(rest as AProps)} />;
        } else {
          return <UnauthorizedComponent {...rest} />;
        }
      }
    }

    //@ts-ignore - displayName is a valid field
    withAuthorization.displayName = `withAuthorization(${getDisplayName(
      AuthorizedComponent,
    )} ? ${getDisplayName(UnauthorizedComponent)})`;

    return connect<StoreProps, {}, OwnProps>(
      (store): StoreProps => ({
        roles: sessionSelector.getRoles(store),
      }),
      {}, // mapDispatchToProps is a no-op to prevent leaking dispatch to children
    )(withAuthorization);
  };
};

/**
 * Higher order component for selectively rendering based on the authorization
 * roles of the current user.  Each property is a function that accepts two
 * parameters: the authorized and unauthorized components.
 */
export const authorizedTo = {
  contactCST: authorizerForRoles(['contact_cse']),
  manageCredentials: authorizerForRoles(['credsmgmt']),
  switchAllCustomers: authorizerForRoles(['custswitch']),
  switchMspOwnCustomers: authorizerForRoles(['mspswitch']),
  viewDashboard: authorizerForRoles(['dashboard']),
  viewData: authorizerForRoles(['dataview']),
  viewRawLogs: authorizerForRoles(['rawlogs']),
  viewTickets: authorizerForRoles(['ticket']),
  viewOnboarding: authorizerForRoles(['onboarding']),
  viewReports: authorizerForRoles(['reports']),
};

export default authorizerForRoles;
